import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { SortingField, SortingFieldName, SortingOrder } from '~/api/Catalog';
import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import { SORTING_OPTIONS } from '~/components/ProductSorting/constants';

import styles from './Sorting.module.scss';

const Sorting = ({
  value,
  onChange,
}: {
  value: SortingField;
  onChange?: (args: [SortingFieldName, SortingOrder]) => void;
}) => {
  const { t } = useTranslation();
  const rootRef = useRef<Nullable<HTMLDivElement>>(null);
  const dropdownRef = useRef<Nullable<HTMLDivElement>>(null);

  const [isOpen, setIsOpen] = useState(false);

  const options = useMemo(
    () =>
      SORTING_OPTIONS.filter((v) => v !== value).map((v) => ({
        label: t(`sorting:${v}`),
        value: v,
      })),
    [value],
  );

  const onChangeSelectedValue = (value: string) => {
    const [field, order] = value.split('.') as [SortingFieldName, SortingOrder];

    onChange?.([field, order]);
    setIsOpen(false);
  };

  const getDropdownPositionStyles = (): CSSProperties | undefined => {
    const rect = rootRef.current?.getBoundingClientRect();

    if (!rect) {
      return;
    }

    return {
      left: rect.left - 24,
      top: rect.top + rect.height + 12,
      minWidth: rect.width,
    };
  };

  const renderDropDown = () => {
    if (typeof document === 'undefined' || !isOpen) {
      return null;
    }

    const content = (
      <div
        className={styles.dropdown}
        ref={dropdownRef}
        style={getDropdownPositionStyles()}
      >
        {options.map((item) => (
          <div
            key={item.value}
            className={styles.option}
            role="button"
            tabIndex={-1}
            onClick={() => onChangeSelectedValue(item.value)}
          >
            {item.label}
          </div>
        ))}
      </div>
    );

    return createPortal(content, document.body);
  };

  useEffect(() => {
    const listener = (e: Event) => {
      const node = e.target as HTMLElement;

      if (
        rootRef.current?.contains(node) ||
        dropdownRef.current?.contains(node)
      ) {
        return;
      }

      setIsOpen(false);
    };

    window.addEventListener('click', listener);

    return () => {
      window.removeEventListener('click', listener);
    };
  }, []);

  return (
    <>
      <div
        className={classes([
          styles.root,
          company.isMarketplaceVariant && styles.rootMarketplace,
          isOpen && styles.open,
        ])}
        role="button"
        tabIndex={-1}
        onClick={() => setIsOpen((prev) => !prev)}
        ref={rootRef}
      >
        <span>{t(`sorting:${value}`)}</span>
        <Icon type="chevron-3.1" size={16} />
      </div>
      {renderDropDown()}
    </>
  );
};

export default observer(Sorting);
